// Held window heights: a window that folds to one line for a moment.
//
// A ringing Clock (and a folded MeetingDebrief or Memory line) shrinks its
// window to the height of one slim bar and grows back when it opens again.
// The shrunken height is a session-only display state. It must never reach
// the saved workspace, another renderer, a scene or a verification snapshot:
// a reload, or any later save of the whole workspace, would otherwise keep the
// window at bar height for good.
//
// The store records the height each held window had when it folded. Every
// path that leaves this renderer writes the "saved view" below, which puts
// that height back. The live component list keeps the slim height so the
// window renders and occupies one line. Release writes the held height back
// into the live list.
//
// Only a short fold (a ringing Clock) also reserves the space it grows back
// into, for placement. A fold that can last all session (a handled
// MeetingDebrief, a Memory line) would otherwise keep invisible empty space
// under it that no window may use.

import type { componentsMapType } from "@/src/shared/contracts/app";

/**
 * The space a short fold keeps free while it is folded. `overlapping` lists
 * the windows that already overlapped the unfolded window when it folded,
 * each with the rectangle it had then (`geometryKey`), and `at` is where the
 * folded window was then (`positionKey`). Growing back over one of those, in
 * the same spot, is the layout the person had, so it does not move the window.
 */
export type HeldGrowth = { at: string; overlapping: Readonly<Record<string, string>> };

/**
 * The heights a window had when it folded, per layout. `mobileHeight` null: no
 * mobile size of its own then. `growth` null: a fold that can last, which
 * reserves no space (only saves keep its height).
 */
export type HeldComponentHeight = { windowId: string; height: number; mobileHeight: number | null; growth: HeldGrowth | null };

export type HeldComponentHeights = Readonly<Record<string, HeldComponentHeight>>;

/** A window's desktop position, as recorded in `HeldGrowth.at`. */
export const positionKey = (component: Pick<componentsMapType, "position">) => `${component.position.x},${component.position.y}`;

/** A window's desktop rectangle, as recorded in `HeldGrowth.overlapping`. */
export const geometryKey = (component: Pick<componentsMapType, "position" | "size">) =>
  `${component.position.x},${component.position.y},${component.size.width},${component.size.height}`;

/** The holds that reserve the space their window grows back into (see HeldComponentHeight.growth). */
export function growthHolds(held: HeldComponentHeights): HeldComponentHeights {
  const entries = Object.entries(held);
  const reserving = entries.filter(([, hold]) => hold.growth !== null);
  return reserving.length === entries.length ? held : Object.fromEntries(reserving);
}

type Sized = Pick<componentsMapType, "size" | "mobileSize">;

/** `component` with the heights it had before it folded, at its current widths. */
export function withHeldHeight<T extends Sized>(component: T, held: Pick<HeldComponentHeight, "height" | "mobileHeight">): T {
  // Keys keep their order, so a held and a released copy of the same window serialize alike.
  const restored: T = { ...component, size: { ...component.size, height: held.height } };
  if (held.mobileHeight !== null) {
    return { ...restored, mobileSize: { ...(component.mobileSize ?? component.size), height: held.mobileHeight } };
  }
  if (!("mobileSize" in restored)) return restored;
  const { mobileSize: _folded, ...rest } = restored;
  return rest as T;
}

/** What may be saved, synced or captured for `components`: held windows at their unfolded heights. */
export function savedComponents<T extends Sized & { id: string }>(
  components: readonly T[],
  held: HeldComponentHeights,
): T[] {
  return components.map((component) => {
    const hold = held[component.id];
    return hold ? withHeldHeight(component, hold) : component;
  });
}

/** `savedComponents` for every window of a componentsByWindow map. */
export function savedComponentsByWindow<T extends Sized & { id: string }>(
  componentsByWindow: Readonly<Record<string, T[]>>,
  held: HeldComponentHeights,
): Record<string, T[]> {
  if (Object.keys(held).length === 0) return { ...componentsByWindow };
  return Object.fromEntries(
    Object.entries(componentsByWindow).map(([windowId, components]) => [windowId, savedComponents(components, held)]),
  );
}

/**
 * After a synced or saved workspace replaced this renderer's windows, held
 * windows keep the slim height they have here: the incoming copy carries the
 * unfolded height, and the bar's content would not change size to fit it again.
 */
export function keepHeldLiveHeights<T extends Sized & { id: string }>(
  mergedByWindow: Record<string, T[]>,
  localByWindow: Readonly<Record<string, T[]>>,
  held: HeldComponentHeights,
): Record<string, T[]> {
  const heldIds = Object.keys(held);
  if (heldIds.length === 0) return mergedByWindow;
  const live = new Map<string, T>();
  for (const components of Object.values(localByWindow)) {
    for (const component of components) if (held[component.id]) live.set(component.id, component);
  }
  if (live.size === 0) return mergedByWindow;
  return Object.fromEntries(
    Object.entries(mergedByWindow).map(([windowId, components]) => [
      windowId,
      components.map((component) => {
        const local = live.get(component.id);
        if (!local) return component;
        const { mobileSize, ...rest } = component;
        const kept = { ...rest, size: { ...component.size, height: local.size.height } } as T;
        return local.mobileSize ? { ...kept, mobileSize: { ...(mobileSize ?? local.mobileSize), height: local.mobileSize.height } } : kept;
      }),
    ]),
  );
}

/**
 * How a hold that reserves growth starts over when an incoming copy puts its
 * window somewhere new (see rebaseHeldHeights): `localByWindow` is this
 * renderer's copy before the merge, and `regrow` records the growth at the
 * incoming row, the way a fold records it.
 */
export type HeldGrowthRebase<T extends Sized & { id: string; position: { x: number; y: number } }> = {
  localByWindow: Readonly<Record<string, readonly T[]>>;
  regrow: (windowId: string, incoming: T) => HeldGrowth;
};

/**
 * The holds after `incomingByWindow` replaced this renderer's copy of some
 * windows (a sync from another renderer, a scene switch, a recovery, a
 * delivered completion). The incoming rows carry the unfolded height that is
 * now saved, so a held window grows back to that height, in the window it now
 * belongs to, not to the one it had when it folded. Pass only the incoming
 * rows: the live rows of held windows are slim. Same object when nothing changes.
 *
 * With `growth`, a window the incoming copy puts somewhere other than where it
 * is here (a scene, another renderer) starts its growth over at that spot: the
 * windows it overlaps there are that layout, not windows placed into the space
 * it grows back into. A move made here (a drag while folded) arrives at the
 * spot it already has, so its growth, and the windows that count as new, stay.
 */
export function rebaseHeldHeights<T extends Sized & { id: string; position: { x: number; y: number } }>(
  incomingByWindow: Readonly<Record<string, readonly T[]>>,
  held: HeldComponentHeights,
  growth?: HeldGrowthRebase<T>,
): HeldComponentHeights {
  if (Object.keys(held).length === 0) return held;
  const localPositions = new Map<string, string>();
  if (growth) {
    for (const components of Object.values(growth.localByWindow)) {
      for (const component of components) if (held[component.id]?.growth) localPositions.set(component.id, positionKey(component));
    }
  }
  let rebased: Record<string, HeldComponentHeight> | null = null;
  for (const [windowId, components] of Object.entries(incomingByWindow)) {
    for (const component of components) {
      const hold = held[component.id];
      if (!hold) continue;
      const mobileHeight = component.mobileSize?.height ?? null;
      const placedElsewhere = growth && hold.growth && localPositions.get(component.id) !== positionKey(component);
      const nextGrowth = placedElsewhere ? growth.regrow(windowId, component) : hold.growth;
      if (hold.windowId === windowId && hold.height === component.size.height && hold.mobileHeight === mobileHeight
        && nextGrowth === hold.growth) continue;
      rebased ??= { ...held };
      rebased[component.id] = { windowId, height: component.size.height, mobileHeight, growth: nextGrowth };
    }
  }
  return rebased ?? held;
}

/** `held` without the holds of `ids`. Same object when none of them is held. */
export function withoutHolds(held: HeldComponentHeights, ids: Iterable<string>): HeldComponentHeights {
  let kept: Record<string, HeldComponentHeight> | null = null;
  for (const id of ids) {
    if (!held[id]) continue;
    kept ??= { ...held };
    delete kept[id];
  }
  return kept ?? held;
}
